import { useEffect, type RefObject } from 'react';

/**
 * Calls `handler` on mousedown outside the referenced element. Used
 * by all the depletions dropdown menus (Sort / Filter / State multi-
 * select / Yearly sort) so clicking anywhere outside the open popover
 * dismisses it — the previous behaviour required clicking the toggle
 * button again to close.
 *
 * The `enabled` flag should be `true` only while the menu is open;
 * otherwise we waste a global listener for nothing.
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const onMouseDown = (e: MouseEvent) => {
      const node = ref.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      handler();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [ref, handler, enabled]);
}
